import { create } from 'zustand';

import type { UIComponent } from '@nexus/ari-assistant-ui';
import { persistSelectedLanguage, type Auth0User, type Auth0Session } from '@nexus/supabase';

import i18n from './i18n';
import { getLanguageFromI18 } from '../config/languages';

import type { Language } from '../config/languages';

type ToastType = 'info' | 'error' | 'success';

type Store = {
  // Country selection
  selectedCountry: Language;
  setSelectedCountry: (country: Language) => void;

  // Toast notifications
  toast: {
    open: boolean;
    message: string;
    type: ToastType;
  };
  showToast: (message: string, type: ToastType) => void;
  hideToast: () => void;
  clearToast: () => void;

  // Sidebar
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleSidebar: () => void;

  // Developer Mode — gate for dev-only features. Persisted to localStorage under
  // 'ari-cloud-debug', mirroring the ari app's developerMode. Currently gates the
  // personal/business account-type test toggle (see accountType below).
  developerMode: boolean;
  setDeveloperMode: (enabled: boolean) => void;

  // Test-only: simulate a personal vs business account so both profile/account
  // variants can be previewed. Personal upgrades via /upgrade; business is
  // managed on the Ari Business portal. Remove once real account data is wired.
  accountType: 'personal' | 'business';
  setAccountType: (type: 'personal' | 'business') => void;

  // Authentication state (uses Auth0)
  supabaseUser: Auth0User | null;
  supabaseSession: Auth0Session | null;
  setSupabaseUser: (user: Auth0User | null) => void;
  setSupabaseSession: (session: Auth0Session | null) => void;
  /** The backend says the session ended in a deliberate sign-out (CLOUD-716). */
  explicitLogout: boolean;
  setExplicitLogout: (explicitLogout: boolean) => void;

  // Current-chat tracking — sidebar / drawer use this to highlight
  // the active row. Chat messages themselves are fetched by the lib's
  // useChat hook via TanStack Query, not stored here.
  currentChatId: string | null;
  setCurrentChatId: (chatId: string | null) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;

  // Generic action confirmation. Mirrors apps/ari's mechanism so confirm/cancel
  // artifacts in the chat-history view render and resolve their buttons. The
  // history view has no live agent, so a resolved promise has no remote
  // consumer — the mechanism drives the UI and keeps store parity with ari.
  actionConfirmation: {
    open: boolean;
    title: string | null;
    details: Record<string, string> | null;
    ui: UIComponent | null;
    resolve: ((value: boolean) => void) | null;
  };
  showActionConfirmation: (request: {
    title: string;
    details?: Record<string, string> | null;
    ui?: UIComponent | null;
  }) => Promise<boolean>;
  confirmAction: () => void;
  cancelAction: () => void;
};

const ACCOUNT_TYPE_KEY = 'ari-cloud:accountType';
const DEVELOPER_MODE_KEY = 'ari-cloud-debug';

function readDeveloperMode(): boolean {
  try {
    return localStorage.getItem(DEVELOPER_MODE_KEY) === 'true';
  } catch {
    return false;
  }
}

// Account type is a dev-only test affordance gated by Developer Mode. With dev
// mode OFF the UI always behaves as a personal account — even if 'business' was
// persisted in a previous dev session — so a fresh load never leaks the test
// variant. Only when dev mode is ON do we honour the stored override.
function getInitialAccountType(): 'personal' | 'business' {
  if (!readDeveloperMode()) return 'personal';
  try {
    return localStorage.getItem(ACCOUNT_TYPE_KEY) === 'business' ? 'business' : 'personal';
  } catch {
    return 'personal';
  }
}

const useStore = create<Store>((set, get) => ({
  selectedCountry: getLanguageFromI18(i18n.language),
  setSelectedCountry: (country) => {
    // Update i18n language when country changes
    i18n.changeLanguage(country.language);
    persistSelectedLanguage(country.language);
    set({ selectedCountry: country });
  },

  toast: {
    open: false,
    message: '',
    type: 'info',
  },
  showToast: (message, type) => set({ toast: { open: true, message, type } }),
  hideToast: () => set((state) => ({ toast: { ...state.toast, open: false } })),
  clearToast: () => set({ toast: { open: false, message: '', type: 'info' } }),

  // Sidebar
  sidebarCollapsed: true,
  setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),
  toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),

  developerMode: readDeveloperMode(),
  setDeveloperMode: (enabled) => {
    try {
      localStorage.setItem(DEVELOPER_MODE_KEY, String(enabled));
    } catch {
      // ignore (e.g. storage disabled)
    }
    if (!enabled) {
      // Turning dev mode off snaps the whole UI back to a personal account
      // reactively (no reload) and clears the persisted test override so it
      // can't resurface on the next load.
      try {
        localStorage.setItem(ACCOUNT_TYPE_KEY, 'personal');
      } catch {
        // ignore (e.g. storage disabled)
      }
      set({ developerMode: false, accountType: 'personal' });
      return;
    }
    set({ developerMode: true });
  },

  // Persisted to localStorage so the test toggle survives reloads (only while
  // dev mode is on) and applies consistently across the dashboard and account
  // page. See getInitialAccountType for how dev mode gates the initial value.
  accountType: getInitialAccountType(),
  setAccountType: (type) => {
    try {
      localStorage.setItem(ACCOUNT_TYPE_KEY, type);
    } catch {
      // ignore (e.g. storage disabled)
    }
    set({ accountType: type });
  },

  // Authentication (uses Auth0)
  supabaseUser: null,
  supabaseSession: null,
  setSupabaseUser: (user) => set({ supabaseUser: user }),
  setSupabaseSession: (session) => set({ supabaseSession: session }),
  explicitLogout: false,
  setExplicitLogout: (explicitLogout) => set({ explicitLogout }),

  currentChatId: null,
  setCurrentChatId: (chatId) => set({ currentChatId: chatId }),

  searchQuery: '',
  setSearchQuery: (query) => set({ searchQuery: query }),

  actionConfirmation: { open: false, title: null, details: null, ui: null, resolve: null },
  showActionConfirmation: ({ title, details, ui }) =>
    new Promise<boolean>((resolve) => {
      set({ actionConfirmation: { open: true, title, details: details ?? null, ui: ui ?? null, resolve } });
    }),
  confirmAction: () => {
    get().actionConfirmation.resolve?.(true);
    set({ actionConfirmation: { open: false, title: null, details: null, ui: null, resolve: null } });
  },
  cancelAction: () => {
    get().actionConfirmation.resolve?.(false);
    set({ actionConfirmation: { open: false, title: null, details: null, ui: null, resolve: null } });
  },
}));

export { useStore };
export type { Store };
